import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import {
  FaBox, FaCheckCircle, FaClock, FaFolderOpen, FaUsers,
  FaArrowRight, FaEye, FaStar, FaPlus, FaFileImport,
  FaTimes, FaChartBar, FaRocket, FaFire, FaExternalLinkAlt,
  FaGlobe
} from 'react-icons/fa';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/settings/stats')
      .then(res => { setStats(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <div className="rf-loading-spinner" />
        </div>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>
          Failed to load dashboard data.
        </div>
      </AdminLayout>
    );
  }

  const publishedPct = stats.totalResources > 0
    ? Math.round((stats.publishedResources / stats.totalResources) * 100) : 0;

  return (
    <AdminLayout>
      {/* Welcome Header */}
      <div className="db-welcome">
        <div>
          <h2 className="db-welcome-title">
            Welcome back, <span className="db-welcome-name">{user?.username || 'Admin'}</span>
          </h2>
          <p className="db-welcome-sub">Here's what's happening with your resource directory.</p>
        </div>
        <div className="db-welcome-actions">
          <Link to="/admin/resources/new" className="rf-btn rf-btn-save">
            <FaPlus /> Add Resource
          </Link>
          <Link to="/" className="rf-btn rf-btn-cancel" target="_blank">
            <FaGlobe /> View Site
          </Link>
        </div>
      </div>

      {/* Pending Alert */}
      {stats.pendingResources > 0 && (
        <div className="db-alert-pending">
          <div className="db-alert-left">
            <div className="db-alert-icon"><FaClock /></div>
            <div>
              <strong>{stats.pendingResources} resource{stats.pendingResources > 1 ? 's' : ''} pending approval</strong>
              <span>Review and publish to make them visible on the site</span>
            </div>
          </div>
          <Link to="/admin/resources?status=pending" className="db-alert-btn">
            Review Now <FaArrowRight />
          </Link>
        </div>
      )}

      {/* Stats Cards */}
      <div className="db-stats">
        <div className="db-stat-card">
          <div className="db-stat-icon db-stat-purple"><FaBox /></div>
          <div className="db-stat-info">
            <span className="db-stat-num">{stats.totalResources.toLocaleString()}</span>
            <span className="db-stat-label">Total Resources</span>
          </div>
          <div className="db-stat-bar">
            <div className="db-stat-bar-fill" style={{ width: '100%', background: 'linear-gradient(90deg, #6366f1, #818cf8)' }} />
          </div>
        </div>

        <div className="db-stat-card">
          <div className="db-stat-icon db-stat-green"><FaCheckCircle /></div>
          <div className="db-stat-info">
            <span className="db-stat-num">{stats.publishedResources.toLocaleString()}</span>
            <span className="db-stat-label">Published ({publishedPct}%)</span>
          </div>
          <div className="db-stat-bar">
            <div className="db-stat-bar-fill" style={{ width: `${publishedPct}%`, background: 'linear-gradient(90deg, #22c55e, #4ade80)' }} />
          </div>
        </div>

        <div className="db-stat-card">
          <div className="db-stat-icon db-stat-amber"><FaClock /></div>
          <div className="db-stat-info">
            <span className="db-stat-num">{stats.pendingResources}</span>
            <span className="db-stat-label">Pending</span>
          </div>
        </div>

        <div className="db-stat-card">
          <div className="db-stat-icon db-stat-cyan"><FaEye /></div>
          <div className="db-stat-info">
            <span className="db-stat-num">{stats.totalViews.toLocaleString()}</span>
            <span className="db-stat-label">Total Views</span>
          </div>
        </div>

        <div className="db-stat-card">
          <div className="db-stat-icon db-stat-pink"><FaFolderOpen /></div>
          <div className="db-stat-info">
            <span className="db-stat-num">{stats.totalCategories}</span>
            <span className="db-stat-label">Categories</span>
          </div>
        </div>

        <div className="db-stat-card">
          <div className="db-stat-icon db-stat-blue"><FaUsers /></div>
          <div className="db-stat-info">
            <span className="db-stat-num">{stats.totalUsers}</span>
            <span className="db-stat-label">Users</span>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="db-columns">
        {/* Left Column */}
        <div className="db-col">
          {/* Top Categories */}
          <div className="db-panel">
            <div className="db-panel-header">
              <h3><FaChartBar /> Top Categories</h3>
              <Link to="/admin/categories" className="db-panel-link">View All <FaArrowRight /></Link>
            </div>
            <div className="db-cat-list">
              {stats.topCategories?.map((cat, i) => {
                const maxCount = stats.topCategories[0]?.count || 1;
                const pct = Math.max(5, Math.round((cat.count / maxCount) * 100));
                return (
                  <div key={i} className="db-cat-item">
                    <div className="db-cat-rank">#{i + 1}</div>
                    <div className="db-cat-info">
                      <span className="db-cat-name">{cat.name}</span>
                      <div className="db-cat-bar-wrap">
                        <div className="db-cat-bar" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="db-cat-count">{cat.count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="db-panel">
            <div className="db-panel-header">
              <h3><FaRocket /> Quick Actions</h3>
            </div>
            <div className="db-actions-grid">
              <Link to="/admin/resources/new" className="db-action-card">
                <div className="db-action-icon" style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8' }}><FaPlus /></div>
                <span>Add Resource</span>
              </Link>
              <Link to="/admin/categories" className="db-action-card">
                <div className="db-action-icon" style={{ background: 'rgba(236,72,153,0.1)', color: '#f472b6' }}><FaFolderOpen /></div>
                <span>Categories</span>
              </Link>
              <Link to="/admin/import" className="db-action-card">
                <div className="db-action-icon" style={{ background: 'rgba(20,184,166,0.1)', color: '#2dd4bf' }}><FaFileImport /></div>
                <span>Import Data</span>
              </Link>
              <Link to="/admin/users" className="db-action-card">
                <div className="db-action-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#fbbf24' }}><FaUsers /></div>
                <span>Users</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="db-col">
          {/* Recently Added */}
          <div className="db-panel">
            <div className="db-panel-header">
              <h3><FaClock /> Recently Added</h3>
              <Link to="/admin/resources" className="db-panel-link">View All <FaArrowRight /></Link>
            </div>
            <div className="db-resource-list">
              {stats.recentResources?.map(r => (
                <div key={r.id} className="db-resource-item">
                  <img
                    src={r.icon_url || `https://www.google.com/s2/favicons?domain=${encodeURIComponent(r.link)}&sz=32`}
                    alt=""
                    className="db-resource-icon"
                    onError={e => { e.target.src = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(r.link)}&sz=32`; }}
                  />
                  <div className="db-resource-info">
                    <span className="db-resource-name">{r.name}</span>
                    <span className="db-resource-meta">
                      {r.category_name || 'Uncategorized'} &middot; {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <span className={`db-resource-status db-status-${r.status}`}>
                    {r.status === 'published' ? <FaCheckCircle /> : r.status === 'pending' ? <FaClock /> : <FaTimes />}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Most Viewed */}
          <div className="db-panel">
            <div className="db-panel-header">
              <h3><FaFire /> Most Viewed</h3>
            </div>
            <div className="db-resource-list">
              {stats.topViewed?.map((r, i) => (
                <div key={r.id} className="db-resource-item">
                  <div className="db-view-rank">
                    {i < 3 ? <FaStar style={{ color: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : '#cd7f32' }} /> : <span>#{i + 1}</span>}
                  </div>
                  <img
                    src={r.icon_url || `https://www.google.com/s2/favicons?domain=${encodeURIComponent(r.link)}&sz=32`}
                    alt=""
                    className="db-resource-icon"
                    onError={e => { e.target.src = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(r.link)}&sz=32`; }}
                  />
                  <div className="db-resource-info">
                    <span className="db-resource-name">{r.name}</span>
                    <span className="db-resource-meta">{r.category_name || 'Uncategorized'}</span>
                  </div>
                  <span className="db-view-count"><FaEye /> {r.views.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default Dashboard;
